import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { TrendingUp, LogOut, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const LANGS = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
  { code: 'es', label: 'ES' },
];

const MENU_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', path: '/dashboard' },
  { id: 'news', label: 'News', path: '/news' },
  { id: 'portfolio', label: 'Portfolio', path: '/portfolio' },
  { id: 'gems', label: 'Pépites', path: '/gems' },
  { id: 'academie', label: 'Académie', path: '/academie' },
  { id: 'simulator', label: 'Simulateur', path: '/simulator' },
  { id: 'pro-analysis', label: 'Analyse Pro', path: '/pro-analysis' },
  { id: 'recommendations', label: 'Recommandations', path: '/recommendations' },
  { id: 'chat', label: 'AI Chat', path: '/chat' },
  { id: 'chart-analyzer', label: 'Chart Analyzer', path: '/chart-analyzer' },
  { id: 'checkout', label: "S'abonner", path: '/checkout' },
];

function MobileNav({ currentPage = 'dashboard' }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { language, changeLanguage } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="glass border-b border-[#ff6b35]/10">
      <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">

        {/* Logo */}
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#ff6b35] to-[#ff8555] flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-[#0a0a0a]" />
            </div>
            <span className="text-xl font-bold gradient-text">Do Not Bull</span>
          </div>

          {/* Desktop nav links */}
          <div className="hidden md:flex gap-6">
            {MENU_ITEMS.map(item => (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                className={currentPage === item.id ? 'text-[#ff6b35] font-semibold' : 'text-gray-400 hover:text-[#ff6b35] transition-colors'}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2">

          {/* Language switcher — 3 simple buttons, works on all devices */}
          <div className="flex items-center gap-0.5 bg-zinc-900 rounded-full px-1 py-1 border border-zinc-800">
            {LANGS.map(lang => (
              <button
                key={lang.code}
                data-testid={`lang-${lang.code}`}
                onClick={() => changeLanguage(lang.code)}
                className={`px-2.5 py-1 rounded-full text-xs font-bold transition-all ${
                  language === lang.code
                    ? 'bg-[#ff6b35] text-black'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {lang.label}
              </button>
            ))}
          </div>

          <span className="hidden sm:block text-gray-300 text-sm ml-1">{user?.name}</span>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-gray-400 hover:text-[#ff6b35] transition-colors ml-1"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          {/* Desktop logout */}
          <Button
            onClick={logout}
            variant="ghost"
            size="icon"
            className="hidden md:flex text-gray-400 hover:text-[#ff6b35]"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Mobile menu dropdown */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-[#ff6b35]/10 bg-[#0a0a0a]">
          <div className="px-6 py-4 space-y-3">
            {MENU_ITEMS.map(item => (
              <button
                key={item.id}
                onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                className={`block w-full text-left py-2 ${
                  currentPage === item.id ? 'text-[#ff6b35] font-semibold' : 'text-gray-400 hover:text-[#ff6b35] transition-colors'
                }`}
              >
                {item.label}
              </button>
            ))}
            <div className="border-t border-gray-700 pt-3 mt-3">
              <button
                onClick={logout}
                className="flex items-center gap-2 text-gray-400 hover:text-[#ff6b35] transition-colors py-2"
              >
                <LogOut className="w-5 h-5" />
                {language === 'fr' ? 'Déconnexion' : language === 'es' ? 'Cerrar sesión' : 'Log out'}
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

export default MobileNav;
